import React from 'react';

import { useWatch, useFormContext } from 'react-hook-form';
import { InputBox, LbInput, LbSelect, ReadOnlyInput } from '@infoods/lifebase-ui';

import {
  LbAutocomplete,
  ModeAwareInput,
  PhoneNumberInput,
} from '../../../shared/components/form-components';
import { BUSINESS_TYPE, businessTypeOptions } from '../../constants/businessType';
import {
  validateUrl,
  validateEmail,
  validateOneWord,
  validateZipCodeFormat,
} from '../../../shared/utils/reactHookFormUtils';
import { corporationTypeOptions } from '../../constants/corporationType';
import { states } from '../../../shared/constants/states';
import { getPhoneView } from '../../../shared/utils/stringUtils';
import { Mode } from '../../../general-types';
import { EntityProfileInfo } from '../../interfaces/EntityProfileInfo';
import { availableEntityTypeOptions } from './EntityBusinessInfoInputs.settings';
import { renderSolutionsValue } from './utils/renderSolutionsValue';

type EntityBusinessInfoFromValue = {
  businessInfo: Omit<EntityProfileInfo, 'paymentFlowType'>;
};

type EntityBusinessInfoInputsProps = {
  mode: 'EDIT' | 'CREATE' | 'VIEW';
};

// If difficult manege paymentFlowType set it by default from props, update add on creation entity
const EntityBusinessInfoInputs = ({ mode }: EntityBusinessInfoInputsProps) => {
  const { control } = useFormContext<EntityBusinessInfoFromValue>();

  const selectedBusinessType = useWatch({ name: 'businessInfo.businessType', control });
  const isCorporateBusiness = selectedBusinessType === BUSINESS_TYPE.BUSINESS;

  const generalInputProps = { control, size: 'small' } as const;
  const privateInfoProps = { InputProps: { className: 'private-info' } };
  /** blur only the value, label is not private */
  const privateValueClassName = 'private-info';

  const isCreate = mode === 'CREATE';
  const fieldMode: Mode = mode === 'VIEW' ? 'VIEW' : 'EDIT';
  const modeAwareProps = { mode: fieldMode, control } as const;

  return (
    <div>
      {isCreate && (
        <InputBox title="General Entity Setup">
          {/* can be replaced with SolutionsSelect when need return ability to select solutions */}
          <ReadOnlyInput
            {...generalInputProps}
            name="businessInfo.solutions"
            label="Solution Type"
            renderValue={renderSolutionsValue}
          />
          <LbSelect
            {...generalInputProps}
            name="businessInfo.entityType"
            label="Entity Type"
            options={availableEntityTypeOptions}
            rules={{ required: 'Entity Type is required' }}
          />
        </InputBox>
      )}
      <InputBox title="Entity Info" mode={fieldMode}>
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.businessName"
          label="Friendly Business Name"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{ required: 'Friendly Business Name is Required' }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.businessEmail"
          label="Entity Email"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Entity Email is required',
                validate: { validateEmail },
              }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.businessPhone"
          label="Entity Phone number"
          valueClassName={privateValueClassName}
          renderValue={getPhoneView}
          renderEdit={(props) => (
            <PhoneNumberInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Entity Phone number is required',
              }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.businessLegalName"
          label="Legal Business Name"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{ required: 'Legal Business Name is required' }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.businessType"
          label="Business Type"
          options={businessTypeOptions}
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbSelect
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Business Type is required',
              }}
              {...privateInfoProps}
            />
          )}
        />
        {isCorporateBusiness && (
          <>
            <ModeAwareInput
              {...modeAwareProps}
              name="businessInfo.businessStructure.ein"
              label="EIN"
              valueClassName={privateValueClassName}
              renderEdit={(props) => (
                <LbInput
                  {...generalInputProps}
                  {...props}
                  rules={{ required: 'EIN is required' }}
                  shouldUnregister
                  {...privateInfoProps}
                />
              )}
            />
            <ModeAwareInput
              {...modeAwareProps}
              name="businessInfo.businessStructure.corporationType"
              label="Corporation Type"
              options={corporationTypeOptions}
              renderEdit={(props) => (
                <LbSelect
                  {...generalInputProps}
                  {...props}
                  rules={{ required: 'Corporation Type is required' }}
                  shouldUnregister
                />
              )}
            />
            <ModeAwareInput
              {...modeAwareProps}
              name="businessInfo.businessStructure.businessTradeNameDBA"
              label="DBA (Business Trade Name)"
              renderEdit={(props) => (
                <LbInput
                  {...generalInputProps}
                  {...props}
                  rules={{ required: 'DBA is required' }}
                  shouldUnregister
                />
              )}
            />
          </>
        )}
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.url"
          label="Website"
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                validate: { validateUrl },
              }}
            />
          )}
        />
      </InputBox>
      <InputBox title="Address" mode={fieldMode}>
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.address"
          label="Address"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{ required: 'Address is required' }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.city"
          label="City"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{ required: 'City is Required' }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.state"
          label="State"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbAutocomplete
              {...generalInputProps}
              {...props}
              options={states}
              rules={{ required: 'State is required' }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.zip"
          label="Zip Code"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Zip Code is required',
                validate: { validateZipCodeFormat },
              }}
              {...privateInfoProps}
            />
          )}
        />
      </InputBox>
      <InputBox title="Info about owner" mode={fieldMode}>
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.firstName"
          label="First Owner Name"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'First Owner Name is required',
                validate: { validateOneWord },
              }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.lastName"
          label="Last Owner Name"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Last Owner Name is required',
                validate: { validateOneWord },
              }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.phoneNumber"
          label="Business Owner Phone"
          valueClassName={privateValueClassName}
          renderValue={getPhoneView}
          renderEdit={(props) => (
            <PhoneNumberInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Business Owner Phone is required',
              }}
              {...privateInfoProps}
            />
          )}
        />
        <ModeAwareInput
          {...modeAwareProps}
          name="businessInfo.userEmail"
          label="Business Owner Email"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Business Owner Email is required',
                validate: { validateEmail },
              }}
              {...privateInfoProps}
            />
          )}
        />
      </InputBox>
    </div>
  );
};

export {
  EntityBusinessInfoInputs,
  EntityBusinessInfoFromValue,
  EntityBusinessInfoInputsProps,
};
